import React, { useEffect, useState, useRef } from "react";
import styles from './YoutubeSection.module.css';

import { fetchChannelVideos } from "../../utils/youtubeUtils";
import {
  getCachedYoutubeData,
  saveYoutubeData
} from "../../utils/firebaseUtils";

const CHANNEL_ID = "UCPF4bAZimS4T8w1TlbeIAYg";
const UPLOADS_ID = "UUPF4bAZimS4T8w1TlbeIAYg";

// --- Reusable Scroll Reveal Hook ---
// (Recommendation: Move this to a src/hooks/useScrollReveal.js file later for global use)
const useScrollReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.animateIn);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 } // Triggers when 10% of the grid is visible
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const YoutubeSection = () => {
  const [visibleCount, setVisibleCount] = useState(6);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const gridRef = useScrollReveal();

  useEffect(() => {
    const loadYoutubeData = async () => {
      setIsLoading(true);
      
      try {
        // 1️⃣ Check cache
        const cached = await getCachedYoutubeData(CHANNEL_ID);

        if (cached?.videos?.length) {
          setVideos(cached.videos);
          setIsLoading(false);
          return;
        }

        // 2️⃣ Cache expired → fetch fresh data
        const freshVideos = await fetchChannelVideos(UPLOADS_ID, 18);

        // 3️⃣ Save to Firestore
        await saveYoutubeData(CHANNEL_ID, {
          videos: freshVideos
        });

        setVideos(freshVideos);
      } catch (error) {
        console.error("Error loading YouTube videos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadYoutubeData();
  }, []);

  const handleLoadMore = () => {
    if (visibleCount < videos.length) {
      setVisibleCount(v => v + 6);
    } else {
      window.open(`https://www.youtube.com/channel/${CHANNEL_ID}`, "_blank");
    }
  };

  return (
    <section className={styles.youtubeContainer} id="youtube">
      <div ref={gridRef} className={`${styles.gridStyle} ${styles.revealUp}`}>
        
        {/* SKELETON LOADERS */}
        {isLoading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className={styles.cardStyle}>
              <div className={`${styles.videoWrapper} ${styles.skeletonBg}`}></div>
              <div className={styles.videoInfo}>
                <div className={styles.skeletonText}></div>
                <div className={styles.skeletonText} style={{ width: '60%', marginTop: '8px' }}></div>
              </div>
            </div>
          ))
        ) : (
          /* ACTUAL VIDEOS */
          videos.slice(0, visibleCount).map(video => {
            const videoId = video.snippet?.resourceId?.videoId;
            if (!videoId) return null;

            return (
              <div key={videoId} className={styles.cardStyle}>
                <div className={styles.videoWrapper}>
                  <iframe
                    className={styles.iframeStyle}
                    src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                    title={video.snippet.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className={styles.videoInfo}>
                  <h3 className={styles.videoTitle}>{video.snippet.title}</h3>
                  <div className={styles.playOverlay}>
                    <span>WATCH NOW</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!isLoading && videos.length > 0 && (
        <div className={styles.actionContainer}>
          <button className={styles.buttonStyle} onClick={handleLoadMore}>
            {visibleCount < videos.length ? "LOAD MORE REVIEWS" : "VIEW FULL CHANNEL"}
          </button>
        </div>
      )}
    </section>
  );
};

export default YoutubeSection;